// src/MagazineManagement.js
import React, { useState, useMemo, useEffect } from "react";
import { Button, Table } from "react-bootstrap";
import { Link } from "react-router-dom";
import { Modal, Form, Card } from "react-bootstrap";

import axios from "axios";
import { BASE_URL, IMAGE_URL, security_key } from "../../../global";
import Swal from "sweetalert2";
import LinearProgress from "@mui/material/LinearProgress";

const MagazineManagement = () => {
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [filterStatus, setFilterStatus] = useState("all");
    const [filterCategory, setFilterCategory] = useState("all");
    const [magazines, setMagazines] = useState([]);
    const [selectedMagazine, setSelectedMagazine] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [selectedRows, setSelectedRows] = useState([]);
    const [sortConfig, setSortConfig] = useState({
        key: "mag_name",
        direction: "asc",
    });
    const [currentPage, setCurrentPage] = useState(1);

    const handleDelete = async (mag_key) => {
        try {
            const result = await Swal.fire({
                title: "Are you sure?",
                text: "You won't be able to revert this!",
                icon: "warning",
                showCancelButton: true,
                confirmButtonColor: "#3085d6",
                cancelButtonColor: "#d33",
                confirmButtonText: "Yes, delete it",
            });

            if (result.isConfirmed) {
                const response = await axios.post(BASE_URL + "API/apicall", {
                    security_key: security_key,
                    mode: "deleteMagazine",
                    pk_mag_key: mag_key,
                });

                const output = response.data;

                if (output) {
                    await Swal.fire("Deleted!", "The magazine has been successfully removed.", "success");
                    fetchAllMagazine();
                }
            }
        } catch (error) {
            // Handle any errors here
            console.error("An error occurred: ", error);
        }
    };

    const handleSelectAll = (e) => {
        if (e.target.checked) {
            setSelectedRows(magazines.map((magazine) => magazine.pk_mag_key));
        } else {
            setSelectedRows([]);
        }
    };

    const handleRowSelect = (pk_mag_key) => {
        setSelectedRows((prevSelectedRows) =>
            prevSelectedRows.includes(pk_mag_key) ? prevSelectedRows.filter((rowId) => rowId !== pk_mag_key) : [...prevSelectedRows, pk_mag_key]
        );
    };

    useEffect(() => {
        fetchAllMagazine();
    }, []);

    const fetchAllMagazine = async () => {
        setLoading(true);
        const response = await axios.post(BASE_URL + "API/apicall", {
            security_key: security_key,
            mode: "getAllMagazine",
        });

        const output = response.data;

        if (output.length > 0) {
            setMagazines(output);
        }
        setLoading(false);
    };

    const handlePageChange = (pageNumber) => {
        setCurrentPage(pageNumber);
    };

    const filteredMagazines = useMemo(() => {
        handlePageChange(1);
        return magazines.filter((magazine) => {
            return magazine.mag_name.toLowerCase().includes(searchTerm.toLowerCase());
        });
    }, [magazines, searchTerm, filterStatus, filterCategory]);

    const sortedMagazines = useMemo(() => {
        const sorted = [...filteredMagazines];
        sorted.sort((a, b) => {
            if (a[sortConfig.key] < b[sortConfig.key]) {
                return sortConfig.direction === "asc" ? -1 : 1;
            }
            if (a[sortConfig.key] > b[sortConfig.key]) {
                return sortConfig.direction === "asc" ? 1 : -1;
            }
            return 0;
        });
        return sorted;
    }, [filteredMagazines, sortConfig]);

    const ITEMS_PER_PAGE = 10;

    const paginatedMagazines = useMemo(() => {
        const start = (currentPage - 1) * ITEMS_PER_PAGE;
        const end = start + ITEMS_PER_PAGE;
        return sortedMagazines.slice(start, end);
    }, [sortedMagazines, currentPage]);

    const totalPages = Math.ceil(sortedMagazines.length / ITEMS_PER_PAGE);

    const handleSort = (key) => {
        setSortConfig((prevSortConfig) => ({
            key,
            direction: prevSortConfig.direction === "asc" ? "desc" : "asc",
        }));
    };

    const handleShowModal = (magazine) => {
        setSelectedMagazine(magazine);
        setShowModal(true);
    };

    const handleCloseModal = () => setShowModal(false);

    return (
        <div className="container mt-4">
            <div className="d-flex justify-content-between align-items-center admin-page-head">
                <h4 className="mb-2">Magazine Management</h4>
                <Button as={Link} to="/admin/add-magazine" variant="dark" className="mb-3">
                    Add New Magazine
                </Button>
            </div>
            <div className="mb-4">
                <Form>
                    <div className="row">
                        <div className="col-md-4">
                            <Form.Group>
                                <Form.Control type="text" placeholder="Search by name" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
                            </Form.Group>
                        </div>
                    </div>
                </Form>
            </div>

            <div className="table-responsive">
                <Table bordered striped>
                    <thead className="thead-dark">
                        <tr>
                            {/* <th>
                                <input type="checkbox" onChange={handleSelectAll} checked={selectedRows.length === magazines.length} />
                            </th> */}
                            <th>Logo</th>
                            <th onClick={() => handleSort("mag_name")} style={{ cursor: "pointer" }}>
                                Name{" "}
                                {sortConfig.key === "mag_name" ? (
                                    sortConfig.direction === "asc" ? (
                                        <i class="fa-solid fa-caret-up"></i>
                                    ) : (
                                        <i class="fa-solid fa-caret-down"></i>
                                    )
                                ) : (
                                    ""
                                )}
                            </th>
                            <th onClick={() => handleSort("mag_link")} style={{ cursor: "pointer" }}>
                                Link{" "}
                                {sortConfig.key === "mag_link" ? (
                                    sortConfig.direction === "asc" ? (
                                        <i class="fa-solid fa-caret-up"></i>
                                    ) : (
                                        <i class="fa-solid fa-caret-down"></i>
                                    )
                                ) : (
                                    ""
                                )}
                            </th>
                            <th onClick={() => handleSort("mag_type")} style={{ cursor: "pointer" }}>
                                Magazine Type{" "}
                                {sortConfig.key === "mag_type" ? (
                                    sortConfig.direction === "asc" ? (
                                        <i class="fa-solid fa-caret-up"></i>
                                    ) : (
                                        <i class="fa-solid fa-caret-down"></i>
                                    )
                                ) : (
                                    ""
                                )}
                            </th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading && (
                            <tr>
                                <td colSpan={4} className="p-0">
                                    <LinearProgress />
                                </td>
                            </tr>
                        )}
                        {!loading && paginatedMagazines.length == 0 && (
                            <tr>
                                <td colSpan={4}>
                                    <h6 className="text-center">Data not available</h6>
                                </td>
                            </tr>
                        )}
                        {paginatedMagazines.map((magazine) => (
                            <tr key={magazine.pk_mag_key} className="border-0">
                                {/* <td>
                                    <input
                                        type="checkbox"
                                        checked={selectedRows.includes(magazine.pk_mag_key)}
                                        onChange={() => handleRowSelect(magazine.pk_mag_key)}
                                    />
                                </td> */}
                                <td>
                                    <img src={IMAGE_URL + magazine.mag_logo_mod} alt={magazine.mag_name} className="table-img" />
                                </td>
                                <td>{magazine.mag_name}</td>
                                <td>{magazine.mag_link}</td>
                                <td>
                                    {magazine.mag_type === 1 ? "Paperboy" : magazine.mag_type === 2 ? "Manual" : magazine.mag_type === 3 ? "Feed" : "Unknown"}
                                </td>

                                <td className="">
                                    <div className="actbtn-grp flex-wrap flex-md-nowrap">
                                        <Button
                                            as={Link}
                                            to={`/admin/edit-magazine/${magazine.pk_mag_key}`}
                                            className="edit-icon btn btn-outline-dark"
                                            data-bs-toggle="tooltip"
                                            title="Edit"
                                        >
                                            <i class="fa-solid fa-pen-to-square"></i>
                                        </Button>
                                        <Button
                                            onClick={() => handleDelete(magazine.pk_mag_key)}
                                            className="btn btn-outline-danger"
                                            data-bs-toggle="tooltip"
                                            title="Delete"
                                        >
                                            <i className="fa-solid fa-trash"></i>
                                        </Button>
                                        <Button
                                            onClick={() => handleShowModal(magazine)}
                                            className="btn btn-outline-info"
                                            data-bs-toggle="tooltip"
                                            title="View"
                                        >
                                            <i className="fa-solid fa-eye"></i>
                                        </Button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </Table>
            </div>
            <div className="pagination d-flex justify-content-end mb-3 my-3">
                <Button onClick={() => handlePageChange(1)} disabled={currentPage === 1} className="btn btn-light mx-1">
                    &lt;&lt;
                </Button>
                <Button onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} className="btn btn-light mx-1">
                    &lt;
                </Button>
                {Array.from({ length: totalPages }, (_, index) => (
                    <Button
                        key={index + 1}
                        onClick={() => handlePageChange(index + 1)}
                        className={`mx-1 ${currentPage === index + 1 ? "btn-dark text-white" : "btn-light"}`}
                    >
                        {index + 1}
                    </Button>
                ))}
                <Button onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages} className="btn btn-light mx-1">
                    &gt;
                </Button>
                <Button onClick={() => handlePageChange(totalPages)} disabled={currentPage === totalPages} className="btn btn-light mx-1">
                    &gt;&gt;
                </Button>
            </div>

            {selectedMagazine && (
                <Modal show={showModal} onHide={handleCloseModal} size="lg">
                    <Modal.Header closeButton>
                        <Modal.Title>{selectedMagazine.mag_name}</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <div className="mt-3">
                            <img src={IMAGE_URL + selectedMagazine.mag_logo_mod} alt="Featured" style={{ width: "100%", height: "auto" }} />
                        </div>
                        <div className="mt-3">
                            <h5>Link</h5>
                            <p>{selectedMagazine.mag_link}</p>
                        </div>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={handleCloseModal}>
                            Close
                        </Button>
                    </Modal.Footer>
                </Modal>
            )}

            <style jsx>
                {`
                    .table-img {
                        width: 100px; /* Adjust as needed */
                        height: auto;
                    }
                    th {
                        cursor: pointer;
                    }
                    .pagination {
                        margin-top: 20px;
                    }
                    .edit-icon:hover {
                        color: #fff !important;
                    }
                    .actbtn-grp {
                        display: flex;
                        gap: 6px;
                        flex-wrap: wrap;
                    }
                `}
            </style>
        </div>
    );
};

export default MagazineManagement;
